import { useCallback } from 'react';
import { useReactFlow } from '@xyflow/react';
import { nanoid } from 'nanoid';
import { toast } from 'sonner';
import { textToTiptapJSON } from '@/lib/utils';
import { useI18n } from '@/providers/i18n';

export type SplitMode = 'paragraph' | 'line' | 'sentence' | 'comma';

export function useTextSplit(id: string) {
  const { t } = useI18n();
  const { getNode, addNodes, addEdges } = useReactFlow();

  const handleSplit = useCallback((text: string, mode: SplitMode = 'paragraph', extraData?: any) => {
    const currentText = (text || '').trim();
    if (!currentText) {
      toast.error(t('No text to split'));
      return;
    }

    let parts: string[] = [];
    if (mode === 'paragraph') {
      parts = currentText.split(/\r?\n\s*\r?\n+/);
    } else if (mode === 'line') {
      parts = currentText.split(/\r?\n+/);
    } else if (mode === 'sentence') {
      // 改进正则表达式，支持中英文结尾标点，且不强制要求后跟空格
      const splitParts = currentText.split(/([.!?。！？])\s*/);
      const result: string[] = [];
      for (let i = 0; i < splitParts.length; i += 2) {
        const txt = splitParts[i];
        const punc = splitParts[i + 1] || '';
        if (txt || punc) result.push((txt + punc).trim());
      }
      parts = result;
    } else if (mode === 'comma') {
      parts = currentText.split(/[,，]\s*/);
    }

    parts = parts.map((b) => b.trim()).filter((b) => b.length > 0);

    if (parts.length <= 1) {
      toast.error(t('No multiple parts found to split'));
      return;
    }

    const node = getNode(id);
    if (!node) return;

    const baseX = node.position.x;
    const baseY = node.position.y;
    const width = (node as any).measured?.width ?? 300;
    const gap = 240;
    const verticalGap = 160;

    const newNodes: any[] = [];
    const newEdges: any[] = [];

    parts.forEach((block, index) => {
      const newId = nanoid();
      const posX = baseX + width + gap;
      const posY = baseY + index * verticalGap;
      const newContent = textToTiptapJSON(block);
      
      newNodes.push({ 
        id: newId, 
        type: 'text', 
        position: { x: posX, y: posY }, 
        data: { 
          viewMode: 'transform',
          content: newContent, 
          text: block,
          ...(extraData || {})
        }, 
        origin: [0, 0.5] 
      });
      
      newEdges.push({ 
        id: nanoid(), 
        source: id, 
        target: newId, 
        type: 'animated' 
      });
    });

    addNodes(newNodes);
    addEdges(newEdges);

    toast.success(t('Split into {{count}} nodes', { count: parts.length }));
  }, [id, getNode, t, addNodes, addEdges]);

  return { handleSplit };
}
